/**
 * TripPage — the simplified Trip screen (T27.2.7.9.3).
 *
 * Shows the household's shopping list grouped by the store each item is picked
 * at (reusing the shared `groupByStore`), plus a "grab anywhere" group for items
 * with no store chosen. Read-only: picking a store happens in Planning (the
 * `handlePickOffer` path); this view is where that pick visibly pays off.
 *
 * Deliberately thin. The full routed itinerary — store → store → home routing,
 * per-store totals, savings, per-item price (that is .9.5), the route line with
 * distance + time, on-your-way suggestions, a Home stop, re-plan — is future
 * work and is NOT built here. This is grouping only: a store header + read-only
 * item rows.
 *
 * One mobile-first responsive surface; desktop is the same app, widened/centered
 * by the shell wrapper. All styles flow from tokens (CP5).
 */

import { useMemo } from 'react';
import { useActiveShoppingList } from '@shopista/shared/api/hooks';
import type { ShoppingListItemWithDetails } from '@shopista/shared/types';
import { Card } from '../../components';
import { groupByStore } from '../lists/groupByStore';
import { shoppingListItemThumb } from '../lists/shoppingListItemThumb';
import * as s from './tripStyles';

export function TripPage(): JSX.Element {
  const listQuery = useActiveShoppingList();

  const items = listQuery.data?.items ?? [];
  // The trip shows what is still to be bought — pending items only. Purchased
  // items are out of the trip (you already grabbed them).
  const pendingItems = useMemo(
    () => items.filter((it) => it.status === 'pending'),
    [items],
  );
  const groups = useMemo(() => groupByStore(pendingItems), [pendingItems]);

  if (listQuery.isError) {
    return (
      <Card padding="lg" data-testid="trip-error">
        <p>Could not load your trip.</p>
        <p style={s.errorStyle}>
          {listQuery.error instanceof Error
            ? listQuery.error.message
            : String(listQuery.error)}
        </p>
      </Card>
    );
  }

  return (
    <div style={s.pageWrapperStyle} data-testid="trip-page">
      <h2 style={s.titleStyle}>Your trip</h2>
      <p style={s.subStyle} data-testid="trip-sub">
        Your list, grouped by where you&apos;ll buy each item. Pick a store for
        an item and it moves into that store&apos;s group.
      </p>

      {pendingItems.length === 0 ? (
        <Card padding="lg" data-testid="trip-empty">
          <div style={s.emptyCardStyle}>
            <div style={s.emptyEmojiStyle} aria-hidden="true">
              🧭
            </div>
            <p>Nothing to plan a trip for yet.</p>
            <p>Add items in Planning, then pick where to buy each one.</p>
          </div>
        </Card>
      ) : (
        groups.map((group) => (
          <div
            key={group.key}
            style={s.storeGroupStyle}
            data-testid={`trip-group-${group.key}`}
          >
            <div style={s.storeHeadStyle}>
              <span style={s.storeHeadNameStyle}>{group.label}</span>
              <span
                style={s.storeHeadCountStyle}
                data-testid={`trip-group-${group.key}-count`}
              >
                {group.items.length}{' '}
                {group.items.length === 1 ? 'item' : 'items'}
              </span>
            </div>
            {group.items.map((item) => (
              <TripItemRow key={item.id} item={item} />
            ))}
          </div>
        ))
      )}
    </div>
  );
}

function TripItemRow({
  item,
}: {
  item: ShoppingListItemWithDetails;
}): JSX.Element {
  const brand = item.offer_brand ?? null;
  const qty = item.quantity != null && item.quantity > 1 ? item.quantity : null;

  return (
    <div style={s.itemRowStyle} data-testid={`trip-item-${item.id}`}>
      <span style={s.thumbStyle} aria-hidden="true">
        {shoppingListItemThumb(item)}
      </span>
      <span style={s.itemMetaStyle}>
        <span style={s.itemNameStyle}>{item.display_title}</span>
        {(brand || qty) && (
          <span style={s.itemSubStyle}>
            {brand && <span>{brand}</span>}
            {brand && qty && <span aria-hidden="true">·</span>}
            {qty && <span>×{qty}</span>}
          </span>
        )}
      </span>
    </div>
  );
}
